import { i18n } from "@lingui/core";
import { useLingui } from "@lingui/react/macro";
import { isModelRequiredSendError, normalizeRunError } from "@rakazo/core";
import { X } from "lucide-react";
import type { RefObject } from "react";
import { useEffect, useRef } from "react";
import { translateRunError } from "../../lib/run-error";

export function ComposerError({
  sendError,
  runError,
  runErrorId,
  onRunErrorPresented,
  onOpenModels,
  onDismissError,
  textareaRef,
}: {
  sendError: string | null;
  runError: string | null;
  runErrorId: string | null;
  onRunErrorPresented: (runId: string) => void;
  onOpenModels: () => void;
  onDismissError: () => void;
  textareaRef: RefObject<HTMLTextAreaElement | null>;
}) {
  const { t } = useLingui();
  const runErrorRef = useRef<HTMLDivElement>(null);
  const presentedRunErrorIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!runError || !runErrorId) return;
    const currentRunErrorId = runErrorId;
    if (presentedRunErrorIdRef.current === currentRunErrorId) return;
    let intersecting = false;
    let frame: number | undefined;
    let mutations: MutationObserver | undefined;
    function stopRechecking() {
      mutations?.disconnect();
      mutations = undefined;
      document.removeEventListener("transitionend", scheduleCheck);
      if (frame !== undefined) cancelAnimationFrame(frame);
      frame = undefined;
    }
    function cleanup() {
      stopRechecking();
      observer?.disconnect();
      document.removeEventListener("visibilitychange", recordIfPresented);
    }
    function scheduleCheck() {
      if (frame !== undefined) return;
      frame = requestAnimationFrame(() => {
        frame = undefined;
        recordIfPresented();
      });
    }
    function recordIfPresented() {
      const element = runErrorRef.current;
      if (!intersecting || !element || document.visibilityState !== "visible") return;
      if (presentedRunErrorIdRef.current === currentRunErrorId) return;
      const rect = element.getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) return;
      // Without hit testing (older engines, test DOMs) an element with a box is
      // on screen, which is all this check can know.
      if (typeof document.elementFromPoint !== "function") {
        presentedRunErrorIdRef.current = currentRunErrorId;
        cleanup();
        onRunErrorPresented(currentRunErrorId);
        return;
      }
      const topElement = document.elementFromPoint(
        (Math.max(0, rect.left) + Math.min(window.innerWidth, rect.right)) / 2,
        (Math.max(0, rect.top) + Math.min(window.innerHeight, rect.bottom)) / 2,
      );
      if (topElement && (topElement === element || element.contains(topElement))) {
        presentedRunErrorIdRef.current = currentRunErrorId;
        cleanup();
        onRunErrorPresented(currentRunErrorId);
        return;
      }
      // Only watch for an occluder to move while an unseen card is in view.
      if (!mutations) {
        mutations = new MutationObserver(scheduleCheck);
        mutations.observe(document.body, { attributes: true, childList: true, subtree: true });
        document.addEventListener("transitionend", scheduleCheck);
      }
    }
    const element = runErrorRef.current;
    const observer =
      element && typeof IntersectionObserver !== "undefined"
        ? new IntersectionObserver((entries) => {
            const entry = entries.filter((entry) => entry.target === element).at(-1);
            if (!entry) return;
            intersecting = entry.isIntersecting;
            if (intersecting) recordIfPresented();
            else stopRechecking();
          })
        : null;
    document.addEventListener("visibilitychange", recordIfPresented);
    if (observer && element) observer.observe(element);
    else {
      intersecting = true;
      recordIfPresented();
    }
    return cleanup;
  }, [onRunErrorPresented, runError, runErrorId]);
  const normalizedRunError = runError
    ? normalizeRunError(runError, translateRunError, i18n.locale || "en")
    : null;

  return sendError || runError ? (
    <div
      ref={runErrorRef}
      role="alert"
      data-testid="composer-error"
      className="mb-3 flex items-center gap-2 rounded-[14px] border border-destructive/40 bg-destructive/10 px-4 py-2 text-[13px] text-destructive animate-in fade-in-0 slide-in-from-bottom-1 duration-150 motion-reduce:animate-none"
    >
      <div className="min-w-0 flex-1">
        {isModelRequiredSendError(sendError) ? (
          <button
            type="button"
            data-testid="composer-error-open-models"
            onClick={onOpenModels}
            className="text-left underline underline-offset-2 hover:text-foreground"
          >
            {sendError}
          </button>
        ) : (
          (sendError ?? normalizedRunError?.summary ?? null)
        )}
        {!sendError && runError ? (
          <details key={runError} className="mt-1">
            <summary className="w-fit cursor-pointer">{t`Details`}</summary>
            <pre className="mt-2 whitespace-pre-wrap break-words text-xs">
              {normalizedRunError?.details}
            </pre>
          </details>
        ) : null}
      </div>
      <button
        type="button"
        aria-label={t`Dismiss error`}
        data-testid="composer-error-dismiss"
        onClick={() => {
          onDismissError();
          window.requestAnimationFrame(() => textareaRef.current?.focus());
        }}
        className="shrink-0 text-destructive hover:text-foreground"
      >
        <X size={13} strokeWidth={2} />
      </button>
    </div>
  ) : null;
}
